import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ThermostatFlexibilityLevel,
  ThermostatLevel,
  SmartMeterStatus,
  WindStatus,
  HeatWaterStatus,
  PhotovoltaicStatus,
  SpotBatteryStatus,
  SpotHeatPumpStatus,
  SpotHeatWaterStatus,
  SpotSmartMeterStatus,
  PlugStatus,
  SpotBatteryDischargeToGridStatus,
  SpotBatteryDischargeToHomeStatus,
  SpotBatteryChargeFromGridStatus,
} from '../../../core/src/shared/enums';
import { useSelector } from './utils';

type Type =
  | 'wind'
  | 'thermostat'
  | 'thermostatFlexibility'
  | 'smartMeter'
  | 'heatWater'
  | 'photovoltaic'
  | 'plug'
  | 'spotBattery'
  | 'spotHeatPump'
  | 'spotHeatWater'
  | 'spotSmartMeter'
  | 'spotBatteryDischargeToGrid'
  | 'spotBatteryDischargeToHome'
  | 'spotBatteryChargeFromGrid';
type Props =
  | {
      type: Type;
      index?: undefined;
    }
  | {
      type: 'thermostat';
      index: number;
    }
  | {
      type: 'thermostatFlexibility';
      index: number;
    }
  | {
      type: 'smartMeter';
      index: number;
    }
  | {
      type: 'heatWater';
      index: number;
    }
  | {
      type: 'photovoltaic';
      index: number;
    }
  | {
      type: 'plug';
      index: number;
    }
  | {
      type: 'spotBattery';
      index: number;
    }
  | {
      type: 'spotHeatPump';
      index: number;
    }
  | {
      type: 'spotHeatWater';
      index: number;
    }
  | {
      type: 'spotSmartMeter';
      index: number;
    }
  | {
      type: 'spotBatteryDischargeToGrid';
      index: number;
    }
  | {
      type: 'spotBatteryDischargeToHome';
      index: number;
    }
  | {
      type: 'spotBatteryChargeFromGrid';
      index: number;
    };

const mapping = {
  smartMeter: {
    [SmartMeterStatus.None]: (
      <FormattedMessage id="status.smartMeter.none" defaultMessage=" " />
    ),
    [SmartMeterStatus.Consumption]: (
      <FormattedMessage
        id="status.smartMeter.consumption"
        defaultMessage="Běžný odběr domu"
      />
    ),
    [SmartMeterStatus.Overflow]: (
      <FormattedMessage
        id="status.smartMeter.overflow"
        defaultMessage="Přetok energie"
      />
    ),
    [SmartMeterStatus.OverflowLimited]: (
      <FormattedMessage
        id="status.smartMeter.overflowLimited"
        defaultMessage="Přetok omezen z důvodu záporné ceny"
      />
    ),
    [SmartMeterStatus.MaxLoad]: (
      <FormattedMessage
        id="status.smartMeter.maxLoad"
        defaultMessage="Maximální odběr domu"
      />
    ),
    [SmartMeterStatus.Shortage]: (
      <FormattedMessage
        id="status.smartMeter.shortage"
        defaultMessage="Výpadek distribuční sítě"
      />
    ),
  },
  heatWater: {
    [HeatWaterStatus.Stop]: (
      <FormattedMessage id="status.heatWater.stop" defaultMessage="STOP" />
    ),
    [HeatWaterStatus.Legionella]: (
      <FormattedMessage
        id="status.heatWater.legionella"
        defaultMessage="Sanace vody"
      />
    ),
    [HeatWaterStatus.Heating]: (
      <FormattedMessage
        id="status.heatWater.heating"
        defaultMessage="Vytápění teplé vody"
      />
    ),
    [HeatWaterStatus.Flexibility]: (
      <FormattedMessage
        id="status.heatWater.flexibility"
        defaultMessage="Flexibilita aktivní, vytápění teplé vody"
      />
    ),
    [HeatWaterStatus.SpotHeating]: (
      <FormattedMessage
        id="status.heatWater.spotHeating"
        defaultMessage="Vytápění teplé vody ze spotu"
      />
    ),
    [HeatWaterStatus.NotConnected]: (
      <FormattedMessage
        id="status.heatWater.notConnected"
        defaultMessage="Nelze navázat spojení"
      />
    ),
    [HeatWaterStatus.Overload]: (
      <FormattedMessage
        id="status.heatWater.overload"
        defaultMessage="Přetížení sítě, vytápění zastaveno"
      />
    ),
    [HeatWaterStatus.StopSell]: (
      <FormattedMessage
        id="status.heatWater.stopSell"
        defaultMessage="Předehřev blokován, prodej energie"
      />
    ),
    [HeatWaterStatus.StopHDO]: (
      <FormattedMessage
        id="status.heatWater.stopHDO"
        defaultMessage="Ohřev blokován při vysokém tarifu"
      />
    ),
    [HeatWaterStatus.StopSOC]: (
      <FormattedMessage
        id="status.heatWater.stopSOC"
        defaultMessage="Blokace vytápění, nízký stav baterie"
      />
    ),
    [HeatWaterStatus.HWError]: (
      <FormattedMessage
        id="status.heatWater.hwError"
        defaultMessage="Bojler je nahřátý nebo nedostupný"
      />
    ),
    [HeatWaterStatus.TempError]: (
      <FormattedMessage
        id="status.heatWater.tempError"
        defaultMessage="Teplotní čidlo není dostupné"
      />
    ),
    [HeatWaterStatus.InverterError]: (
      <FormattedMessage
        id="status.heatWater.inverterError"
        defaultMessage="Předehřev bojleru neaktivní, není dostupný střídač"
      />
    ),
    [HeatWaterStatus.SmartMeterError]: (
      <FormattedMessage
        id="status.heatWater.smartMeterError"
        defaultMessage="Předehřev bojleru neaktivní, není dostupný elektroměr"
      />
    ),
    [HeatWaterStatus.PVHeating]: (
      <FormattedMessage
        id="status.heatWater.pvHeating"
        defaultMessage="Vytápění z přebytků"
      />
    ),
    [HeatWaterStatus.NotLicensed]: (
      <FormattedMessage
        id="status.heatWater.notLicensed"
        defaultMessage="Řízení bojleru není licencované"
      />
    ),
    [HeatWaterStatus.Off]: (
      <FormattedMessage id="status.heatWater.off" defaultMessage="Vypnuto" />
    ),
  },
  photovoltaic: {
    [PhotovoltaicStatus.Standby]: (
      <FormattedMessage
        id="status.photovoltaic.standby"
        defaultMessage="Stand-by režim"
      />
    ),
    [PhotovoltaicStatus.Production]: (
      <FormattedMessage
        id="status.photovoltaic.production"
        defaultMessage="Výroba energie"
      />
    ),
    [PhotovoltaicStatus.StartUp]: (
      <FormattedMessage
        id="status.photovoltaic.startUp"
        defaultMessage="Startovací režim"
      />
    ),
    [PhotovoltaicStatus.Discharging]: (
      <FormattedMessage
        id="status.photovoltaic.discharging"
        defaultMessage="Vybíjení baterie"
      />
    ),
    [PhotovoltaicStatus.SpotCharging]: (
      <FormattedMessage
        id="status.photovoltaic.spotCharging"
        defaultMessage="Nabíjení baterie ze spotu"
      />
    ),
    [PhotovoltaicStatus.ManualCharging]: (
      <FormattedMessage
        id="status.photovoltaic.manualCharging"
        defaultMessage="Ruční nabíjení baterie"
      />
    ),
    [PhotovoltaicStatus.ManualDischarging]: (
      <FormattedMessage
        id="status.photovoltaic.manualDischarging"
        defaultMessage="Ruční vybíjení baterie"
      />
    ),
    [PhotovoltaicStatus.SpotStopDischarging]: (
      <FormattedMessage
        id="status.photovoltaic.spotStopDischarging"
        defaultMessage="Zastaveno vybíjení, je nízká cena na spotu"
      />
    ),
    [PhotovoltaicStatus.DischargeBlockedByHDO]: (
      <FormattedMessage
        id="status.photovoltaic.dischargeBlockedByHDO"
        defaultMessage="Vybíjení baterie pozastaveno, aktivní vysoký tarif (HDO)"
      />
    ),
    [PhotovoltaicStatus.DischargeBlockedByComponents]: (
      <FormattedMessage
        id="status.photovoltaic.dischargeBlockedByComponents"
        defaultMessage="Vybíjení baterie pozastaveno kvůli spotřebě vybraných technologií"
      />
    ),
    [PhotovoltaicStatus.FlexibilityCharging]: (
      <FormattedMessage
        id="status.photovoltaic.flexibilityCharging"
        defaultMessage="Flexibilita aktivní, nabíjení baterie ze sítě"
      />
    ),
    [PhotovoltaicStatus.FlexibilityDischarging]: (
      <FormattedMessage
        id="status.photovoltaic.flexibilityDischarging"
        defaultMessage="Flexibilita aktivní, vybíjení baterie do sítě"
      />
    ),
    [PhotovoltaicStatus.FlexibilityFeedInPriority]: (
      <FormattedMessage
        id="status.photovoltaic.flexibilityFeedInPriority"
        defaultMessage="Flexibilita aktivní, priorita přetoku do sítě"
      />
    ),
    [PhotovoltaicStatus.FlexibilityDoNotDischarge]: (
      <FormattedMessage
        id="status.photovoltaic.flexibilityDoNotDischarge"
        defaultMessage="Flexibilita aktivní, zákaz vybíjení baterie"
      />
    ),
    [PhotovoltaicStatus.FlexibilityBatteryStop]: (
      <FormattedMessage
        id="status.photovoltaic.flexibilityBatteryStop"
        defaultMessage="Flexibilita aktivní, zastaveno vybíjení i nabíjení baterie"
      />
    ),
    [PhotovoltaicStatus.FlexibilityOverflowRestricted]: (
      <FormattedMessage
        id="status.photovoltaic.flexibilityOverflowRestricted"
        defaultMessage="Flexibilita aktivní, zákaz přetoku do sítě"
      />
    ),
    [PhotovoltaicStatus.FeedInPriority]: (
      <FormattedMessage
        id="status.photovoltaic.feedInPriority"
        defaultMessage="Priorita přetoku do sítě"
      />
    ),
    [PhotovoltaicStatus.PlanCharge]: (
      <FormattedMessage
        id="status.photovoltaic.planCharge"
        defaultMessage="Plánované nabíjení baterie"
      />
    ),
    [PhotovoltaicStatus.PlanDischarge]: (
      <FormattedMessage
        id="status.photovoltaic.planDischarge"
        defaultMessage="Plánované vybíjení baterie"
      />
    ),
    [PhotovoltaicStatus.NotConnected]: (
      <FormattedMessage
        id="status.photovoltaic.notConnected"
        defaultMessage="Nelze navázat spojení"
      />
    ),
    [PhotovoltaicStatus.Idle]: (
      <FormattedMessage
        id="status.photovoltaic.idle"
        defaultMessage="Režim spánku"
      />
    ),
    [PhotovoltaicStatus.SellBatteryEnergy]: (
      <FormattedMessage
        id="status.photovoltaic.sellBatteryEnergy"
        defaultMessage="Prodej energie z baterie"
      />
    ),
    [PhotovoltaicStatus.NotLicensed]: (
      <FormattedMessage
        id="status.photovoltaic.notLicensed"
        defaultMessage="Řízení FVE není licencovaná"
      />
    ),
    [PhotovoltaicStatus.NoneInverter]: (
      <FormattedMessage
        id="status.photovoltaic.noneInverter"
        defaultMessage="Typ střídače nenastaven"
      />
    ),
    [PhotovoltaicStatus.DistributionLimit]: (
      <FormattedMessage
        id="status.photovoltaic.distributionLimit"
        defaultMessage="Distribuce omezuje výkon"
      />
    ),
    [PhotovoltaicStatus.ForcedOverflow]: (
      <FormattedMessage
        id="status.photovoltaic.forcedOverflow"
        defaultMessage="Flexibilita aktivní, vynucený přetok do sítě"
      />
    ),
  },
  wind: {
    [WindStatus.Production]: (
      <FormattedMessage
        id="status.wind.production"
        defaultMessage="Výroba energie"
      />
    ),
    [WindStatus.Stop]: (
      <FormattedMessage id="status.wind.stop" defaultMessage="STOP" />
    ),
    [WindStatus.Unavailable]: (
      <FormattedMessage
        id="status.wind.unavailable"
        defaultMessage="Zařízení není dostupné"
      />
    ),
  },
  thermostat: {
    [ThermostatLevel.Deactivated]: (
      <FormattedMessage
        id="level.thermostat.deactivated"
        defaultMessage="Neaktivní"
      />
    ),
    [ThermostatLevel.Heating]: (
      <FormattedMessage
        id="level.thermostat.heating"
        defaultMessage="Vytápění"
      />
    ),
    [ThermostatLevel.Cooling]: (
      <FormattedMessage
        id="level.thermostat.cooling"
        defaultMessage="Chlazení"
      />
    ),
    [ThermostatLevel.StandBy]: (
      <FormattedMessage
        id="level.thermostat.standby"
        defaultMessage="Standby"
      />
    ),
    [ThermostatLevel.TempErr]: (
      <FormattedMessage
        id="level.thermostat.tempErr"
        defaultMessage="Chyba Teploty"
      />
    ),
  },
  thermostatFlexibility: {
    [ThermostatFlexibilityLevel.IncreasePv]: (
      <FormattedMessage
        id="level.thermostatFlexibility.increasePv"
        defaultMessage="Flexibilita využita pro zvýšení teploty dle fotovoltaiky"
      />
    ),
    [ThermostatFlexibilityLevel.IncreaseSpot]: (
      <FormattedMessage
        id="level.thermostatFlexibility.increaseSpot"
        defaultMessage="Flexibilita využita pro zvýšení teploty dle SPOTU"
      />
    ),
    [ThermostatFlexibilityLevel.Normal]: (
      <FormattedMessage
        id="level.thermostatFlexibility.normal"
        defaultMessage="Normální"
      />
    ),
    [ThermostatFlexibilityLevel.ReductionSpot]: (
      <FormattedMessage
        id="level.thermostatFlexibility.reductionSpot"
        defaultMessage="Flexibilita využita pro snížení teploty dle SPOTU"
      />
    ),
  },
  plug: {
    [PlugStatus.STOP]: (
      <FormattedMessage id="status.plug.stop" defaultMessage="STOP" />
    ),
    [PlugStatus.BatteryControl]: (
      <FormattedMessage
        id="status.plug.batteryControl"
        defaultMessage="Zásuvka aktivní podle baterie"
      />
    ),
    [PlugStatus.SpotControl]: (
      <FormattedMessage
        id="status.plug.spotControl"
        defaultMessage="Zásuvka aktivní podle spotu"
      />
    ),
    [PlugStatus.OverflowControl]: (
      <FormattedMessage
        id="status.plug.overflowControl"
        defaultMessage="Zásuvka aktivní podle přetoku"
      />
    ),
    [PlugStatus.TimeControl]: (
      <FormattedMessage
        id="status.plug.timeControl"
        defaultMessage="Zásuvka aktivní podle času"
      />
    ),
    [PlugStatus.ManualControl]: (
      <FormattedMessage
        id="status.plug.manualControl"
        defaultMessage="Zásuvka aktivní v manuálním módu"
      />
    ),
    [PlugStatus.NotConnected]: (
      <FormattedMessage
        id="status.plug.notConnected"
        defaultMessage="Zásuvka není dostupná"
      />
    ),
    [PlugStatus.NotActive]: (
      <FormattedMessage
        id="status.plug.notActive"
        defaultMessage="Zásuvka neaktivní"
      />
    ),
  },
  spotBattery: {
    [SpotBatteryStatus.FlexibilityPV]: (
      <FormattedMessage
        id="status.spotBattery.flexibilityPV"
        defaultMessage="Flexibilita využita pro FVE"
      />
    ),
    [SpotBatteryStatus.FlexibilitySpot]: (
      <FormattedMessage
        id="status.spotBattery.flexibilitySpot"
        defaultMessage="Flexibilita využita pro SPOT"
      />
    ),
    [SpotBatteryStatus.NoData]: (
      <FormattedMessage
        id="status.spotBattery.noData"
        defaultMessage="Pro řízení nejsou aktuální data"
      />
    ),
    [SpotBatteryStatus.SpotNotLicensed]: (
      <FormattedMessage
        id="status.spotBattery.spotNotLicensed"
        defaultMessage="Spot není licencovaný"
      />
    ),
  },
  spotHeatPump: {
    [SpotHeatPumpStatus.FlexibilityPV]: (
      <FormattedMessage
        id="status.spotHeatPump.flexibilityPV"
        defaultMessage="Flexibilita využita pro FVE"
      />
    ),
    [SpotHeatPumpStatus.FlexibilitySpot]: (
      <FormattedMessage
        id="status.spotHeatPump.flexibilitySpot"
        defaultMessage="Flexibilita využita pro SPOT"
      />
    ),
    [SpotHeatPumpStatus.NoData]: (
      <FormattedMessage
        id="status.spotHeatPump.noData"
        defaultMessage="Pro řízení nejsou aktuální data"
      />
    ),
    [SpotHeatPumpStatus.SpotNotLicensed]: (
      <FormattedMessage
        id="status.spotHeatPump.spotNotLicensed"
        defaultMessage="Spot není licencovaný"
      />
    ),
  },
  spotHeatWater: {
    [SpotHeatWaterStatus.FlexibilityPV]: (
      <FormattedMessage
        id="status.spotHeatWater.flexibilityPV"
        defaultMessage="Flexibilita využita pro FVE"
      />
    ),
    [SpotHeatWaterStatus.FlexibilitySpot]: (
      <FormattedMessage
        id="status.spotHeatWater.flexibilitySpot"
        defaultMessage="Flexibilita využita pro SPOT"
      />
    ),
    [SpotHeatWaterStatus.NoData]: (
      <FormattedMessage
        id="status.spotHeatWater.noData"
        defaultMessage="Pro řízení nejsou aktuální data"
      />
    ),
    [SpotHeatWaterStatus.SpotNotLicensed]: (
      <FormattedMessage
        id="status.spotHeatWater.spotNotLicensed"
        defaultMessage="Spot není licencovaný"
      />
    ),
  },
  spotSmartMeter: {
    [SpotSmartMeterStatus.NoData]: (
      <FormattedMessage
        id="status.spotSmartMeter.noData"
        defaultMessage="Pro řízení nejsou aktuální data"
      />
    ),
    [SpotSmartMeterStatus.SpotNotLicensed]: (
      <FormattedMessage
        id="status.spotSmartMeter.spotNotLicensed"
        defaultMessage="Spot není licencovaný"
      />
    ),
    [SpotSmartMeterStatus.WithoutReduction]: (
      <FormattedMessage
        id="status.spotSmartMeter.withoutReduction"
        defaultMessage="Bez omezení"
      />
    ),
    [SpotSmartMeterStatus.OverflowFlexibilitySell]: (
      <FormattedMessage
        id="status.spotSmartMeter.overflowFlexibilitySell"
        defaultMessage="Optimalizace přetoku pro prodej"
      />
    ),
    [SpotSmartMeterStatus.OverflowDisabled]: (
      <FormattedMessage
        id="status.spotSmartMeter.overflowDisabled"
        defaultMessage="Trvalé omezení přetoku"
      />
    ),
    [SpotSmartMeterStatus.OverflowLimited]: (
      <FormattedMessage
        id="status.spotSmartMeter.overflowLimited"
        defaultMessage="Přetok omezen z důvodu záporné ceny"
      />
    ),
  },
  spotBatteryDischargeToGrid: {
    [SpotBatteryDischargeToGridStatus.SpotNotActive]: (
      <FormattedMessage
        id="status.spotBatteryDischargeToGrid.spotNotActive"
        defaultMessage="Není aktivní spotové obchodování"
      />
    ),
    [SpotBatteryDischargeToGridStatus.BatteryDischargingNotActive]: (
      <FormattedMessage
        id="status.spotBatteryDischargeToGrid.batteryDischargingNotActive"
        defaultMessage="Není aktivované vybíjení do sítě"
      />
    ),
    [SpotBatteryDischargeToGridStatus.MinPriceNotReached]: (
      <FormattedMessage
        id="status.spotBatteryDischargeToGrid.minPriceNotReached"
        defaultMessage="Dnes není splněná minimální cena pro prodej"
      />
    ),
    [SpotBatteryDischargeToGridStatus.NotEnoughBatteryEnergy]: (
      <FormattedMessage
        id="status.spotBatteryDischargeToGrid.notEnoughBatteryEnergy"
        defaultMessage="Dnes se očekává vybití baterie přes noc do vlastní spotřeby"
      />
    ),
    [SpotBatteryDischargeToGridStatus.BatteryDischargingPlanned]: (
      <FormattedMessage
        id="status.spotBatteryDischargeToGrid.batteryDischargingPlanned"
        defaultMessage="Dnes je vybíjení aktivní"
      />
    ),
  },
  spotBatteryDischargeToHome: {
    [SpotBatteryDischargeToHomeStatus.AutomaticDischargeMode]: (
      <FormattedMessage
        id="status.spotBatteryDischargeToHome.automaticDischargeMode"
        defaultMessage="Automatické vybíjení baterie dle kapacity"
      />
    ),
    [SpotBatteryDischargeToHomeStatus.HighPriceDischargeMode]: (
      <FormattedMessage
        id="status.spotBatteryDischargeToHome.highPriceDischargeMode"
        defaultMessage="Vybíjení baterie při vysoké ceně"
      />
    ),
    [SpotBatteryDischargeToHomeStatus.MediumAndHighPriceDischargeMode]: (
      <FormattedMessage
        id="status.spotBatteryDischargeToHome.mediumAndHighPriceDischargeMode"
        defaultMessage="Vybíjení baterie při střední a vysoké ceně"
      />
    ),
    [SpotBatteryDischargeToHomeStatus.NotControlled]: (
      <FormattedMessage
        id="status.spotBatteryDischargeToHome.notControlled"
        defaultMessage="Vybíjení baterie není omezeno"
      />
    ),
  },
  spotBatteryChargeFromGrid: {
    [SpotBatteryChargeFromGridStatus.SpotNotActive]: (
      <FormattedMessage
        id="status.spotBatteryChargeFromGrid.spotNotActive"
        defaultMessage="Není aktivní spotové obchodování"
      />
    ),
    [SpotBatteryChargeFromGridStatus.BatteryChargingNotActive]: (
      <FormattedMessage
        id="status.spotBatteryChargeFromGrid.batteryChargingNotActive"
        defaultMessage="Není aktivované nabíjení ze sítě"
      />
    ),
    [SpotBatteryChargeFromGridStatus.PriceDifferenceTooLow]: (
      <FormattedMessage
        id="status.spotBatteryChargeFromGrid.priceDifferenceTooLow"
        defaultMessage="Dnes není splněný minimální rozdíl ceny"
      />
    ),
    [SpotBatteryChargeFromGridStatus.EnoughPVEnergy]: (
      <FormattedMessage
        id="status.spotBatteryChargeFromGrid.enoughPVEnergy"
        defaultMessage="Dnes se očekává denní přebytek pro nabití baterie"
      />
    ),
    [SpotBatteryChargeFromGridStatus.BatteryChargingPlanned]: (
      <FormattedMessage
        id="status.spotBatteryChargeFromGrid.batteryChargingPlanned"
        defaultMessage="Dnes je nabíjení aktivní"
      />
    ),
  },
};

export const FormattedStatus = ({ type, index }: Props) => {
  const statuses = useSelector(({ variables }) => {
    return {
      wind: variables.WIND_STATUS,
      thermostat: (variables.THERMOSTAT_INFO || []).map((info) => info.status)[
        index ?? 0
      ],
      thermostatFlexibility: (variables.THERMOSTAT_INFO || []).map(
        (info) => info.statusFlexibility
      )[index ?? 0],
      smartMeter: variables.EM_INFO.Status,
      heatWater: variables.HW_INFO.Status,
      photovoltaic: variables.PV_INFO.Status,
      spotBattery: variables.SP_BAT_STATUS,
      spotBatteryDischargeToGrid: variables.SP_DISCHARGE_BATTERY_TO_GRID_STATUS,
      spotBatteryDischargeToHome: variables.SP_DISCHARGE_BATTERY_TO_HOME_STATUS,
      spotBatteryChargeFromGrid: variables.SP_CHARGE_BATTERY_FROM_GRID_STATUS,
      spotHeatWater: variables.SP_HW_STATUS,
      spotHeatPump: variables.SP_HP_STATUS,
      spotSmartMeter: variables.SP_EM_STATUS,
      plug: (variables.PL_INFO || []).map((info) => info.Status)[index ?? 0],
    };
  });

  if (!(type in mapping)) {
    return null;
  }

  const messages = mapping[type] as Record<string, JSX.Element>;
  if (!(statuses[type] in messages)) {
    return null;
  }

  return messages[statuses[type]] as React.ReactElement;
};
